// @ts-nocheck
/**
 * CCMS Shared States — documented stub / showcase page
 *
 * Documents the SDK-adjacent "shared" components (loading / empty / error /
 * error-boundary) and renders each one live with mock props. These components
 * only read a local Zustand preferences store + pure constant maps — they do
 * NOT call any SDK / network / presence / speech hook on mount, so they are all
 * safe to render live in this demo.
 */

import {
  Alert,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { IconCheck, IconFileText, IconShieldCheck } from '@tabler/icons-react';
import {
  CCMSEmptyState,
  CCMSErrorBoundary,
  CCMSErrorState,
  CCMSLoadingState,
} from '@/components/ccms-components/shared';

/**
 * Tiny helper that throws on render so we can demonstrate CCMSErrorBoundary
 * catching an error and showing its default fallback UI — fully contained,
 * never escapes the boundary.
 */
function BoomChild(): never {
  throw new Error('Simulated SDK failure (demo): the file service was unreachable.');
}

/** Reusable "what it needs to run live" badge — same answer for every component here. */
function SafeLiveBadge() {
  return (
    <Group gap="xs">
      <ThemeIcon size="sm" radius="xl" color="green" variant="light">
        <IconCheck size={14} />
      </ThemeIcon>
      <Badge variant="light" color="green">
        nothing — safe to render live with mock props
      </Badge>
    </Group>
  );
}

export default function CcmsSharedPage() {
  return (
    <Stack gap="xl">
      {/* Header */}
      <Stack gap="xs">
        <Title order={2}>CCMS Shared States (Loading / Empty / Error / ErrorBoundary)</Title>
        <Text c="dimmed" size="sm">
          Cross-cutting state components used by every CCMS SDK-bound feature to render
          loading, empty, and error conditions consistently. Each component below is
          documented (purpose, key props, usage snippet) and rendered live with inline
          mock data. None of them call an SDK / network / presence / speech hook on
          mount — they only read the local preferences store and pure style maps — so all
          four are safe to preview here.
        </Text>
        <SafeLiveBadge />
      </Stack>

      {/* CCMSLoadingState */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CCMSLoadingState</Title>
            <Text size="sm" c="dimmed">
              Centered gradient loader with an optional message. Drop it in while an SDK
              query is in flight; pass <Code>fullScreen</Code> to center it in the viewport.
            </Text>
          </Stack>

          <Stack gap={4}>
            <Text size="sm" fw={500}>Key props:</Text>
            <Text size="xs" c="dimmed">message — string — optional (default "Loading...")</Text>
            <Text size="xs" c="dimmed">size — 'xs' | 'sm' | 'md' | 'lg' | 'xl' — optional (default "md")</Text>
            <Text size="xs" c="dimmed">gradientVariant — GradientVariant — optional (default "primary")</Text>
            <Text size="xs" c="dimmed">fullScreen — boolean — optional (default false)</Text>
            <Text size="xs" c="dimmed">className — string — optional</Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Usage:</Text>
            <Code block language="tsx">
{`<CCMSLoadingState message="Loading files..." />
<CCMSLoadingState size="lg" fullScreen />`}
            </Code>
          </Stack>

          <SafeLiveBadge />
          <Divider label="Live preview" labelPosition="center" />
          <Card variant="subtle" p="lg">
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
              <Stack align="center" gap="xs">
                <CCMSLoadingState size="sm" message="Loading files..." />
                <Text size="xs" c="dimmed">size="sm"</Text>
              </Stack>
              <Stack align="center" gap="xs">
                <CCMSLoadingState size="md" message="Fetching records..." />
                <Text size="xs" c="dimmed">size="md"</Text>
              </Stack>
              <Stack align="center" gap="xs">
                <CCMSLoadingState size="lg" gradientVariant="secondary" message="Almost there..." />
                <Text size="xs" c="dimmed">size="lg" · secondary</Text>
              </Stack>
            </SimpleGrid>
          </Card>
        </Stack>
      </Card>

      {/* CCMSEmptyState */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CCMSEmptyState</Title>
            <Text size="sm" c="dimmed">
              Friendly "no data" placeholder with an icon, title, description, and an
              optional call-to-action button. Shown when a list / query returns zero rows.
            </Text>
          </Stack>

          <Stack gap={4}>
            <Text size="sm" fw={500}>Key props:</Text>
            <Text size="xs" c="dimmed">title — string — required</Text>
            <Text size="xs" c="dimmed">description — string — optional</Text>
            <Text size="xs" c="dimmed">icon — ReactNode — optional (default IconInbox)</Text>
            <Text size="xs" c="dimmed">actionLabel — string — optional (renders button when paired with onAction)</Text>
            <Text size="xs" c="dimmed">onAction — () =&gt; void — optional</Text>
            <Text size="xs" c="dimmed">gradientVariant — GradientVariant — optional (default "primary")</Text>
            <Text size="xs" c="dimmed">size — 'sm' | 'md' | 'lg' — optional (default "md")</Text>
            <Text size="xs" c="dimmed">className — string — optional</Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Usage:</Text>
            <Code block language="tsx">
{`<CCMSEmptyState
  title="No files found"
  description="Upload your first file to get started"
  actionLabel="Upload File"
  onAction={() => openUploadModal()}
/>`}
            </Code>
          </Stack>

          <SafeLiveBadge />
          <Divider label="Live preview" labelPosition="center" />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <CCMSEmptyState
              title="No files found"
              description="Upload your first file to get started."
              actionLabel="Upload File"
              onAction={() => {}}
            />
            <CCMSEmptyState
              icon={<IconFileText size={48} />}
              title="No records yet"
              description="Records created in this domain will appear here."
              size="sm"
            />
          </SimpleGrid>
        </Stack>
      </Card>

      {/* CCMSErrorState */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CCMSErrorState</Title>
            <Text size="sm" c="dimmed">
              Inline error panel for a failed operation. Accepts an <Code>Error</Code> or a
              plain string, optionally shows the technical message, and renders a "Try Again"
              button when <Code>onRetry</Code> is supplied.
            </Text>
          </Stack>

          <Stack gap={4}>
            <Text size="sm" fw={500}>Key props:</Text>
            <Text size="xs" c="dimmed">error — Error | string — required</Text>
            <Text size="xs" c="dimmed">title — string — optional (default "An error occurred")</Text>
            <Text size="xs" c="dimmed">showRetry — boolean — optional (default true)</Text>
            <Text size="xs" c="dimmed">onRetry — () =&gt; void — optional (button only renders when present)</Text>
            <Text size="xs" c="dimmed">showDetails — boolean — optional (default false)</Text>
            <Text size="xs" c="dimmed">className — string — optional</Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Usage:</Text>
            <Code block language="tsx">
{`<CCMSErrorState
  error={error}
  title="Failed to load files"
  showRetry
  onRetry={() => refetch()}
/>`}
            </Code>
          </Stack>

          <SafeLiveBadge />
          <Divider label="Live preview" labelPosition="center" />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <CCMSErrorState
              error={new Error('Request failed with status 500')}
              title="Failed to load files"
              showDetails
              onRetry={() => {}}
            />
            <CCMSErrorState
              error="You do not have permission to view this resource."
              title="Access denied"
              showRetry={false}
            />
          </SimpleGrid>
        </Stack>
      </Card>

      {/* CCMSErrorBoundary */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CCMSErrorBoundary</Title>
            <Text size="sm" c="dimmed">
              Class error boundary that catches render-time errors thrown by SDK-bound
              children and shows a recoverable "Something went wrong" UI (or your custom
              <Code>fallback</Code>). Wrap feature trees that talk to the SDK.
            </Text>
          </Stack>

          <Stack gap={4}>
            <Text size="sm" fw={500}>Key props:</Text>
            <Text size="xs" c="dimmed">children — ReactNode — required</Text>
            <Text size="xs" c="dimmed">fallback — (error, reset) =&gt; ReactNode — optional (custom error UI)</Text>
            <Text size="xs" c="dimmed">onError — (error, errorInfo) =&gt; void — optional</Text>
            <Text size="xs" c="dimmed">showDetails — boolean — optional (default false)</Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Usage:</Text>
            <Code block language="tsx">
{`<CCMSErrorBoundary showDetails>
  <FileUploader domain="User" />
</CCMSErrorBoundary>

// With a custom fallback
<CCMSErrorBoundary
  fallback={(error, reset) => (
    <div>
      <p>Error: {error.message}</p>
      <button onClick={reset}>Try Again</button>
    </div>
  )}
>
  <MyComponent />
</CCMSErrorBoundary>`}
            </Code>
          </Stack>

          <Alert variant="info">
            The boundary renders its children unchanged until one of them throws — it is
            safe to mount live. Below: a happy-path child, and a child that throws on render
            so you can see the default fallback the boundary shows (the error is fully
            contained and never crashes the page).
          </Alert>

          <SafeLiveBadge />
          <Divider label="Live preview" labelPosition="center" />
          <Stack gap="lg">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Happy path (children render normally):</Text>
              <CCMSErrorBoundary>
                <Card variant="subtle" p="md">
                  <Group gap="xs">
                    <ThemeIcon color="green" variant="light" radius="xl">
                      <IconShieldCheck size={18} />
                    </ThemeIcon>
                    <Text size="sm">Protected content rendered successfully.</Text>
                  </Group>
                </Card>
              </CCMSErrorBoundary>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Caught error (default fallback with details):</Text>
              <CCMSErrorBoundary showDetails>
                <BoomChild />
              </CCMSErrorBoundary>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
